import { createHttpService } from 'src/apis/ApiService';
import { TPaginationParams } from 'src/apis/__shared/shared.interfaces';
import {
  TRouteApi,
  TRouteAssignmentApi,
  TDriverApi,
  TVehicleApi,
} from 'src/apis/routes/routes.api.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

export type GuardianTransportData = {
  routeAssignment?: TRouteAssignmentApi;
  route?: TRouteApi;
  driver?: TDriverApi;
  vehicle?: TVehicleApi;
  studentStop?: {
    stopId: string;
    stopName: string;
    location: string;
    pickupTime: string;
    dropTime: string;
    stopOrder: number;
  };
  transportFee?: {
    ratePerKm: number;
    distanceFromHome: number;
    totalFee: number;
  };
};

export type GuardianTransportHistory = {
  id: string;
  date: string;
  routeName: string;
  driverName: string;
  driverPhone: string;
  vehicleNumber: string;
  kilometers: number;
  pickupTime: string;
  dropTime: string;
  status: 'completed' | 'missed' | 'cancelled';
  stopName: string;
  location: string;
};

const useGuardianTransportService = () => {
  const httpAPI = createHttpService();
  const baseGuardianPath = '/guardian/transport';
  const withMockData = isAppWithMock();

  const getTransportData = (
    studentId: string
  ): Promise<GuardianTransportData> => {
    if (withMockData) {
      // Mock data - in real implementation, this would fetch from API
      return Promise.resolve({
        routeAssignment: {
          assignmentId: 'assign-1',
          routeId: 'route-1',
          vehicleId: 'vehicle-1',
          driverId: 'driver-1',
          assignedStudents: [studentId],
          academicYear: '2024-2025',
          isActive: true,
          startDate: new Date().toISOString(),
          totalStudents: 1,
        },
        route: {
          routeId: 'route-1',
          routeName: 'Route A - Downtown',
          startLocation: 'School',
          endLocation: 'Downtown',
          estimatedDuration: 45,
          distance: 15,
          isActive: true,
          stops: [
            {
              stopId: 'stop-1',
              stopName: 'Home Stop',
              location: '123 Main St',
              stopOrder: 1,
              pickupTime: '07:30',
              dropTime: '15:30',
              students: [studentId],
            },
          ],
          totalStops: 1,
        },
        driver: {
          driverId: 'driver-1',
          name: 'John Doe',
          licenseNumber: 'DL123456',
          licenseType: 'HEAVY_VEHICLE',
          licenseExpiryDate: new Date('2026-12-31').toISOString(),
          phoneNumber: '+91-9876543210',
          email: 'john.doe@example.com',
          address: '456 Driver St',
          emergencyContact: 'Jane Doe',
          emergencyContactPhone: '+91-9876543211',
          joiningDate: new Date('2020-01-01').toISOString(),
          salary: 30000,
          salaryStatus: 'PAID',
          nextSalaryDueDate: new Date('2025-02-01').toISOString(),
          policeVerificationStatus: 'VERIFIED',
          medicalCertificateExpiryDate: new Date('2025-12-31').toISOString(),
          isActive: true,
        },
        vehicle: {
          vehicleId: 'vehicle-1',
          vehicleNumber: 'KA-01-AB-1234',
          vehicleType: 'BUS',
          capacity: 40,
          driverId: 'driver-1',
          routeId: 'route-1',
          isActive: true,
          lastMaintenanceDate: new Date('2024-12-01').toISOString(),
          nextMaintenanceDate: new Date('2025-03-01').toISOString(),
          fuelType: 'DIESEL',
          registrationNumber: 'KA-01-AB-1234',
          insuranceExpiryDate: new Date('2025-12-31').toISOString(),
          permitExpiryDate: new Date('2025-12-31').toISOString(),
        },
        studentStop: {
          stopId: 'stop-1',
          stopName: 'Home Stop',
          location: '123 Main St',
          pickupTime: '07:30',
          dropTime: '15:30',
          stopOrder: 1,
        },
        transportFee: {
          ratePerKm: 5,
          distanceFromHome: 15,
          totalFee: 75,
        },
      });
    }

    const requestObj = {
      params: {
        studentId,
      },
    };

    return httpAPI
      .get<GuardianTransportData>(baseGuardianPath, requestObj)
      .then((response) => response)
      .catch((error) => {
        console.error('Error fetching guardian transport data:', error);
        throw error;
      });
  };

  const getTransportHistory = (
    studentId: string,
    params?: TPaginationParams & { fromDate?: string; toDate?: string }
  ): Promise<{ data: GuardianTransportHistory[]; count: number }> => {
    if (withMockData) {
      // Mock history data
      const mockHistory: GuardianTransportHistory[] = [
        {
          id: 'hist-1',
          date: new Date('2025-01-30').toISOString(),
          routeName: 'Route A - Downtown',
          driverName: 'John Doe',
          driverPhone: '+91-9876543210',
          vehicleNumber: 'KA-01-AB-1234',
          kilometers: 15,
          pickupTime: '07:30',
          dropTime: '15:30',
          status: 'completed',
          stopName: 'Home Stop',
          location: '123 Main St',
        },
        {
          id: 'hist-2',
          date: new Date('2025-01-29').toISOString(),
          routeName: 'Route A - Downtown',
          driverName: 'John Doe',
          driverPhone: '+91-9876543210',
          vehicleNumber: 'KA-01-AB-1234',
          kilometers: 15,
          pickupTime: '07:35',
          dropTime: '15:25',
          status: 'completed',
          stopName: 'Home Stop',
          location: '123 Main St',
        },
        {
          id: 'hist-3',
          date: new Date('2025-01-28').toISOString(),
          routeName: 'Route A - Downtown',
          driverName: 'John Doe',
          driverPhone: '+91-9876543210',
          vehicleNumber: 'KA-01-AB-1234',
          kilometers: 15,
          pickupTime: '07:30',
          dropTime: '15:30',
          status: 'missed',
          stopName: 'Home Stop',
          location: '123 Main St',
        },
      ];

      let filteredData = [...mockHistory];

      if (params?.fromDate) {
        filteredData = filteredData.filter(
          (h) => h.date >= params.fromDate!
        );
      }
      if (params?.toDate) {
        filteredData = filteredData.filter((h) => h.date <= params.toDate!);
      }

      const start = params?.page && params?.limit
        ? (params.page - 1) * params.limit
        : 0;
      const end = params?.limit ? start + params.limit : filteredData.length;
      const paginatedData = filteredData.slice(start, end);

      return Promise.resolve({
        data: paginatedData,
        count: filteredData.length,
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
        ...(params?.fromDate && { fromDate: params.fromDate }),
        ...(params?.toDate && { toDate: params.toDate }),
      },
    };

    return httpAPI
      .get<{ data: GuardianTransportHistory[]; count: number }>(
        `${baseGuardianPath}/history`,
        requestObj
      )
      .then((response) => {
        const result = response;
        if (Array.isArray(result)) {
          return { data: result, count: result.length };
        }
        return result as { data: GuardianTransportHistory[]; count: number };
      })
      .catch((error) => {
        console.error('Error fetching guardian transport history:', error);
        return { data: [], count: 0 };
      });
  };

  return {
    getTransportData,
    getTransportHistory,
  };
};

export default useGuardianTransportService;
